import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Copy, Download, Linkedin, Share2, ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, date } from "../lib/api";
import { Loading } from "../components/Shell";
import { usePageMeta } from "../lib/meta";

export const Verify = () => {
  usePageMeta("Verify a certificate", "Check that an AVAILITY certificate is genuine and currently valid using its certificate ID.");
  const [params] = useSearchParams();
  const [id, setId] = useState(params.get("certificate") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const lookup = useCallback(async (value) => {
    setError(""); setData(null);
    if (!value.trim()) return;
    try { setData((await api.get(`/certificates/verify/${encodeURIComponent(value.trim())}`)).data); }
    catch { setError("No certificate was found for that ID."); }
  }, []);
  useEffect(() => { const initial = params.get("certificate"); if (initial) lookup(initial); }, [params, lookup]);
  const valid = data?.status === "valid";
  return (
    <main className="verify-page"><div className="verify-box"><div className="verify-seal"><ShieldCheck size={34} /></div><div className="section-kicker">PUBLIC VERIFICATION</div>
      <h1>Check a<br /><span>certificate.</span></h1><p>Enter the unique certificate ID to confirm its holder, credential, and current status.</p>
      <form onSubmit={(e) => { e.preventDefault(); lookup(id); }}><input data-testid="certificate-id-input" value={id} onChange={(e) => setId(e.target.value)} placeholder="Example: AVA-7F2A90C1D4" /><button className="button primary full" data-testid="verify-certificate-button">Verify credential <ShieldCheck size={17} /></button></form>
      {error && <div className="error" data-testid="verification-error">{error}</div>}
      {data && <div className={valid ? "verification-result" : "verification-result revoked"} data-testid="verification-result">{valid ? <CheckCircle2 /> : <ShieldAlert />}
        <div><span data-testid="verification-status">{valid ? "VALID CERTIFICATE" : `CERTIFICATE ${data.status.toUpperCase()}`}</span><strong>{data.certification_title}</strong><p>{data.learner_name} · Issued {date(data.issued_at)}{data.expires_at && ` · ${data.status === "expired" ? "Expired" : "Valid until"} ${date(data.expires_at)}`}{data.revoked_at && ` · Revoked ${date(data.revoked_at)}`}</p>{!valid && <p data-testid="verification-revoked-note">This credential is no longer valid.</p>}<code>{data.certificate_id}</code></div></div>}
    </div></main>
  );
};

export const linkedInAddUrl = (d) => {
  const issued = new Date(d.issued_at);
  const p = new URLSearchParams({ startTask: "CERTIFICATION_NAME", name: d.certification_title, organizationName: "AVAILITY",
    issueYear: String(issued.getFullYear()), issueMonth: String(issued.getMonth() + 1), certUrl: d.verify_url, certId: d.certificate_id });
  if (d.expires_at) { const x = new Date(d.expires_at); p.set("expirationYear", String(x.getFullYear())); p.set("expirationMonth", String(x.getMonth() + 1)); }
  return `https://www.linkedin.com/profile/add?${p.toString()}`;
};

const LinkedInActions = ({ data }) => (
  <div className="linkedin-bar" data-testid="linkedin-actions">
    <span>Show it off on LinkedIn</span>
    <a className="button linkedin" data-testid="linkedin-add-profile-button" href={linkedInAddUrl(data)} target="_blank" rel="noopener noreferrer"><Linkedin size={16} /> Add to profile</a>
    <a className="button outline" data-testid="linkedin-share-button" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(data.verify_url)}`} target="_blank" rel="noopener noreferrer"><Share2 size={16} /> Share post</a>
  </div>
);

const fetchBlob = async (id, fmt) => URL.createObjectURL((await api.get(`/certificates/${id}/${fmt}`, { responseType: "blob" })).data);

const download = async (id, fmt) => {
  try {
    const url = await fetchBlob(id, fmt);
    const a = Object.assign(document.createElement("a"), { href: url, download: `AVAILITY-${id}.${fmt}` });
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch { toast.error("Download failed. Please sign in with the account that owns this certificate."); }
};

const PublicCertificate = ({ data }) => (
  <main className="static-page" data-testid="certificate-public-view"><div className="section-kicker">CERTIFICATE</div><h1>{data.certification_title}</h1>
    <p>Issued to <b>{data.learner_name}</b> on {date(data.issued_at)}. Status: <b data-testid="certificate-public-status">{data.status.toUpperCase()}</b>.</p>
    <p className="muted">Only the certificate holder can view and download this certificate after signing in to AVAILITY.</p>
    <div className="hero-actions"><Link className="button primary" data-testid="certificate-public-verify-link" to={`/verify?certificate=${data.certificate_id}`}>Verify this certificate <ShieldCheck size={16} /></Link>
      <Link className="button outline" data-testid="certificate-public-signin-link" to="/login">Sign in</Link></div>
  </main>
);

export const Certificate = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [image, setImage] = useState(null);
  usePageMeta(data ? `${data.certification_title} — ${data.learner_name}` : "Certificate", "A verifiable AVAILITY certificate.");
  useEffect(() => {
    setError(false); setImage(null);
    api.get(`/certificates/verify/${encodeURIComponent(id)}`).then((r) => setData(r.data)).catch(() => setError(true));
    let url;
    fetchBlob(id, "png").then((u) => { url = u; setImage(u); }).catch(() => setImage(false));
    return () => url && URL.revokeObjectURL(url);
  }, [id]);
  if (error) return <main className="static-page" data-testid="certificate-error"><div className="section-kicker">CERTIFICATE</div><h1>Certificate not found</h1><p>We couldn't find a certificate with ID <code>{id}</code>. Check the link or verify the ID manually.</p><Link className="text-link" data-testid="certificate-error-verify-link" to="/verify">Go to verification <ArrowRight size={16} /></Link></main>;
  if (!data || image === null) return <Loading label="Loading certificate…" />;
  if (image === false) return <PublicCertificate data={data} />;
  const copy = async () => { await navigator.clipboard.writeText(data.verify_url); toast.success("Verification link copied"); };
  const valid = data.status === "valid";
  return (
    <main className="certificate-page wide">
      {!valid && <div className="error revoked-banner" data-testid="certificate-revoked-banner">{data.status === "expired" ? `This certificate expired on ${date(data.expires_at)}.` : `This certificate was revoked${data.revoked_at ? ` on ${date(data.revoked_at)}` : ""} and is no longer valid.`}</div>}
      <div className="cert-toolbar"><div><div className="section-kicker">YOUR CREDENTIAL</div><h1 className="cert-title">{data.certification_title}</h1><p className="muted small">Issued {date(data.issued_at)}{data.expires_at && ` · Valid until ${date(data.expires_at)}`} · <code data-testid="certificate-id">{data.certificate_id}</code></p></div>
        <div className="cert-actions">
          <button className="button primary" data-testid="download-pdf-button" onClick={() => download(data.certificate_id, "pdf")}><Download size={16} /> PDF</button>
          <button className="button secondary" data-testid="download-png-button" onClick={() => download(data.certificate_id, "png")}><Download size={16} /> PNG</button>
          <button className="button outline" data-testid="copy-verify-link-button" onClick={copy}><Copy size={16} /> Copy link</button>
        </div></div>
      {valid && <LinkedInActions data={data} />}
      <img className="certificate-image" data-testid="certificate-image" src={image} alt={`${data.certification_title} certificate for ${data.learner_name}`} />
      <Link className="text-link" data-testid="certificate-verify-link" to={`/verify?certificate=${data.certificate_id}`}>Open public verification page <ArrowRight size={16} /></Link>
    </main>
  );
};
